import type { Rock, Viewport } from "./types";

/**
 * Perspectiva del campo. Funciones puras, sin DOM, usadas tanto por la
 * simulacion como por el render para que nunca discrepen.
 */

export const LINE = (vp: Viewport): number => vp.h - 104;
export const SHIP_Y = (vp: Viewport): number => vp.h - 52;
export const BUFFER_Y = (vp: Viewport): number => vp.h - 16;
/** punto de fuga: hacia donde converge el campo */
export const VP_X = (vp: Viewport): number => vp.w / 2;
export const VP_Y = (vp: Viewport): number => vp.h * 0.1;

/** 0 = lejos (punto de fuga), 1 = sobre la linea de defensa */
export function depthOf(y: number, vp: Viewport): number {
  const h = VP_Y(vp);
  const l = LINE(vp);
  return Math.max(0, Math.min(1.15, (y - h) / (l - h)));
}

/** escala por profundidad: 0.64 al fondo, 1.14 al frente */
export function scaleOf(p: number, depth: number): number {
  return 1 - (1 - (0.64 + 0.5 * p)) * depth;
}

/** radio de la roca; crece con la banda y sobre todo con el numero de etapas */
export function rockRadius(band: number, stages: number, scale: number): number {
  return (17 + Math.min(12, band * 0.22) + (stages - 1) * 11) * scale;
}

/**
 * x en pantalla. Cada roca guarda un carril fijo; la convergencia relajada
 * (0.58 al fondo en vez de 0.22) hace que aparezcan repartidas por todo el
 * campo y no salidas de un solo punto. El vaiven es maximo a media altura y
 * casi nulo junto a la linea: profundidad donde es gratis, estabilidad donde
 * leer mal te cuesta una vida.
 */
export function screenX(
  rock: Rock,
  now: number,
  vp: Viewport,
  depth: number,
  isCandidate: boolean,
  candSway: number,
  reduceMotion: boolean,
): number {
  const p = depthOf(rock.y, vp);
  const spread = 1 - (1 - (0.58 + 0.42 * p)) * depth;
  const bell = Math.sin(Math.max(0, Math.min(1, p)) * Math.PI);
  const damp = isCandidate ? candSway : 1;
  const sway = reduceMotion
    ? 0
    : Math.sin(now * rock.swayFreq + rock.swayPhase) * rock.swayAmp * bell * damp * depth;
  return Math.max(46, Math.min(vp.w - 46, VP_X(vp) + rock.lane * (vp.w * 0.46) * spread + sway));
}
